import { AppConfig } from "../config/app.config";
import { HttpMethod } from "../enums/http-method.enum";
import 'core-js/actual/promise';

interface FetchOptions {
  method?: HttpMethod;
  body?: Record<string, any>;
  timeout?: number;
  strategy?: "first_fulfilled" | "only_primary" | "sequentially"
}

export class HttpProvider {
  constructor(private readonly config: AppConfig) { }

  public async get<T extends Record<string, any>>(url: string, timeout?: number): Promise<T> {
    return this.fetch<T>(url, { timeout, strategy: "first_fulfilled" });
  }

  public async post<T extends Record<string, any>>(url: string, body: object): Promise<T> {
    return this.fetch<T>(url, { method: HttpMethod.POST, body });
  }

  public async put<T extends Record<string, any>>(url: string, body: object): Promise<T> {
    return this.fetch<T>(url, { method: HttpMethod.PUT, body });
  }

  public async patch<T extends Record<string, any>>(url: string, body: object): Promise<T> {
    return this.fetch<T>(url, { method: HttpMethod.PATCH, body });
  }

  public async delete<T extends Record<string, any>>(url: string): Promise<T> {
    return this.fetch<T>(url, { method: HttpMethod.DELETE });
  }

  public async fetch<T extends Record<string, any>>(path: string, options: FetchOptions = { strategy: "sequentially", method: HttpMethod.GET }): Promise<T> {
    if (path.includes("http")) return this.fetchWithTimeout(path, options);
    if (options.strategy) {
      switch (options.strategy) {
        case "first_fulfilled": return this.fetchAll(path, options);
        case "only_primary": return this.fetchPrimary(path, options);
        case "sequentially": return this.fetchSequentially(path, options);
        default: return this.fetchPrimary(path, options);
      }
    } else {
      switch (options.method) {
        case HttpMethod.GET: return this.fetchAll(path, options);
        default: return this.fetchSequentially(path, options);
      }
    }
  }

  private async fetchAll<T extends Record<string, any>>(path: string, options: FetchOptions = {}): Promise<T> {
    const { api_urls } = this.config;

    const response = await Promise.any(
      api_urls.map(url => this.fetchWithTimeout<T>(url + path, options))
    );

    return response;
  }

  private async fetchPrimary<T extends Record<string, any>>(path: string, options: FetchOptions = {}): Promise<T> {
    const { api_urls } = this.config;
    const url = api_urls[0];

    const response = await this.fetchWithTimeout<T>(url + path, options);
    return response;
  }

  private async fetchSequentially<T extends Record<string, any>>(path: string, options: FetchOptions = {}): Promise<T> {
    const { api_urls } = this.config;
    let error: any;

    for (let url of api_urls) {
      try {
        const response = await this.fetchWithTimeout<T>(url + path, options);
        return response;
      } catch (e) {
        error = e;
        console.error(e);
        continue;
      }
    }

    throw new Error(error);
  }

  private async fetchWithTimeout<T extends Record<string, any>>(url: string, options: FetchOptions = {}): Promise<T> {
    const { method = "GET", timeout = 10000, body = undefined } = options;
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), timeout);

    try {
      const response = await fetch(url, {
        method,
        signal: abortController.signal,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      let data: T = await this.parseToJson(response);
      return data;

    } catch (e) {
      console.error(e);
      throw new Error(e as any);
    } finally {
      console.log("finally", url);
      clearTimeout(timeoutId);
    }
  }

  private async parseToJson<T extends Record<string, any>>(response: Response) {
    let data: T = {} as T;
    try {
      data = await response.json();
    } catch (e) {
    } finally {
      return data;
    }
  }
}
